import { IDialogProps } from 'Components/Core/Dialog/types';
import { ChangeEventWithData } from 'Components/Core/types';
import { hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormatTypes, format } from '../../../../Lib/Format';
import { Button, DateInput, Dialog, Field, Message } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { FieldSet } from '../../../Layout';
import { PersonDropdown } from '../WorksheetDropdowns';
import { IWorksheetComponent } from '../types';

const Warnings = {
  OnlySetSelf: Symbol('OnlySetSelf'),
  InsufficientPrivs: Symbol('InsufficientPrivs'),
  NoneAvailable: Symbol('NoneAvailable'),
};

type PersonModel = { Id: Guid; Name: string };

type WorksheetActionByDialogProps = Pick<IDialogProps, 'title' | 'visible' | 'onClose'> &
  IWorksheetComponent & {
    /**
     * The label for the user performing the action
     */
    actionByLabel: string;
    /**
     * The label for the date the action was performed
     */
    actionDateLabel: string;
    /**
     * Uuid of the laboratory context
     */
    laboratoryId: Guid;
    /**
     * Uuid of the worksheet context
     */
    worksheetId: Guid;
    /**
     * Indicates whether this dialog should be rendered as "busy" (i.e. it's parent is processing the set action values)
     */
    busy: boolean;
    /**
     * Callback performed when the action values are submitted
     */
    onSetActionValues: (data: { actionBy: PersonModel | null; actionDate: string | null }) => void;
  };

/**
 * A dialog to select the user performing a worksheet "action" and the date it was performed.
 *
 * This action is currently either "testing" or "checking" the worksheet
 */
let WorksheetActionByDialog: React.FC<WorksheetActionByDialogProps> = ({
  title,
  actionByLabel,
  actionDateLabel,
  laboratoryId,
  worksheetId,
  busy,
  visible,
  onSetActionValues,
  onClose,
}) => {
  const [noAvailablePeople, setNoAvailablePeople] = useState(false);
  const [firstSearchComplete, setFirstSearchComplete] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [loadImmediately, setLoadImmediately] = useState(true);
  const [defaultToMe, setDefaultToMe] = useState(false);
  const [warning, setWarning] = useState<any>(false);
  const dataEntryOperatorClaim = useSelector((s) => s.user.data.Claims['sq.worksheets.dataentryop']);
  const personId = useSelector((s) => s.user.data.PersonId);
  const personName = useSelector((s) => s.user.data.Name);
  const [actionByValue, setActionByValue] = useState<PersonModel | null>(null);
  const [actionDateValue, setActionDateValue] = useState<string | null>(null);
  const isComplete = hasValue(actionByValue) && hasValue(actionDateValue);

  const isSelfAvailableAsTechAtLab = useSelector((s) =>
    s.user.data.TechnicianLaboratoryAvailability.some((l: any) => l.Id === laboratoryId)
  );

  const onPersonListChange = ({ list, search }: any) => {
    // If an empty search comes back with 0 results, then we want to show a warning
    setFirstSearchComplete(true);
    setNoAvailablePeople(search === '' && list && list.length === 0);
  };

  useEffect(() => {
    if (defaultToMe) setActionByValue({ Id: personId, Name: personName });
    else setActionByValue(null);
    setActionDateValue(format({ type: FormatTypes.Date, pattern: 'YYYY-MM-DD' })(new Date()) as string);
  }, [personId, personName, defaultToMe]);

  useEffect(() => {
    if (dataEntryOperatorClaim) {
      // Data entry operator can set tested by, checked by to another user via dropdown
      if (firstSearchComplete) {
        if (isSelfAvailableAsTechAtLab) {
          setDefaultToMe(true);
        } else if (noAvailablePeople) {
          // No users available in dropdown, display warning.
          setDisabled(true);
          setWarning(Warnings.NoneAvailable);
        }
      }
    } else {
      setLoadImmediately(false);
      if (isSelfAvailableAsTechAtLab) {
        setDefaultToMe(true);
        // Disables dropdown
        setWarning(Warnings.OnlySetSelf);
      } else {
        // User not available at worksheet location, display warning.
        setDisabled(true);
        setWarning(Warnings.InsufficientPrivs);
      }
    }
  }, [dataEntryOperatorClaim, firstSearchComplete, noAvailablePeople, isSelfAvailableAsTechAtLab]);

  return (
    <Dialog
      title={title}
      buttons={[
        <Button
          primary
          key="1"
          autoFocus
          onClick={() => onSetActionValues({ actionBy: actionByValue, actionDate: actionDateValue })}
          disabled={!isComplete || disabled}
          loading={busy}
        >
          OK
        </Button>,
        <Button key="0" autoFocus onClick={onClose} disabled={busy}>
          Cancel
        </Button>,
      ]}
      visible={visible}
      onClose={onClose}
      width="400px"
    >
      <FieldSet>
        <Field
          input={<PersonDropdown />}
          searchImmediately={loadImmediately}
          onListChange={(e) => onPersonListChange(e)}
          disabled={disabled || warning === Warnings.OnlySetSelf || busy}
          placeholder={disabled ? `No users found.` : `Select user...`}
          label={actionByLabel}
          queryParams={{ hasActiveUser: true }}
          worksheetId={worksheetId}
          value={actionByValue}
          onChange={({ data: { value } = {} }: ChangeEventWithData) => setActionByValue(value)}
        />
        <Field
          input={<DateInput />}
          disabled={disabled || busy}
          label={actionDateLabel}
          value={actionDateValue}
          onChange={({ data: { value } = {} }: ChangeEventWithData) => setActionDateValue(value)}
        />
        {warning === Warnings.InsufficientPrivs && (
          <Message error>
            You are not available as a laboratory technician at this location. If attempting to set {actionByLabel} to
            another person, ensure you have the Data Entry permission.
          </Message>
        )}
        {warning === Warnings.NoneAvailable && <Message error>No users found. Cannot set {actionByLabel}.</Message>}
      </FieldSet>
    </Dialog>
  );
};

WorksheetActionByDialog = withInjectedProps(WorksheetActionByDialog);

export { WorksheetActionByDialog };
export default WorksheetActionByDialog;
